import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useState, useEffect } from "react";
import { Factory, Loader2 } from "lucide-react";
import { Industry } from "@/types/industry";

interface CreateIndustryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    is_default: boolean;
  }) => Promise<boolean | void>;
  companySlug: string;
  loading?: boolean;
  industryToEdit?: Industry | null;
}

export function CreateIndustryModal({
  isOpen,
  onClose,
  onSave,
  companySlug,
  loading: externalLoading = false,
  industryToEdit,
}: CreateIndustryModalProps) {
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [internalLoading, setInternalLoading] = useState(false);
  const [error, setError] = useState("");

  const loading = externalLoading || internalLoading;

  useEffect(() => {
    if (isOpen) {
      if (industryToEdit) {
        setName(industryToEdit.name);
        setIsDefault(industryToEdit.is_default || false);
      } else {
        setName("");
        setIsDefault(false);
      }
      setError("");
    }
  }, [isOpen, industryToEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!name.trim()) {
      setError("El nombre es obligatorio");
      return;
    }

    if (name.length < 2) {
      setError("El nombre debe tener al menos 2 caracteres");
      return;
    }

    if (name.length > 100) {
      setError("El nombre no puede exceder los 100 caracteres");
      return;
    }

    setInternalLoading(true);
    setError("");

    try {
      await onSave({ name: name.trim(), is_default: isDefault });
      onClose();
    } catch (error: any) {
      console.error(error);
      setError(error.message || "No se pudo guardar la industria.");
    } finally {
      setInternalLoading(false);
    }
  };

  const handleClose = () => {
    setName("");
    setIsDefault(false);
    setError("");
    onClose();
  };

  const handleNameChange = (value: string) => {
    setName(value);
    if (error) setError("");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md sm:max-w-lg p-6 rounded-[24px]">
        <DialogHeader className="mb-4">
          <DialogTitle className="flex items-center text-2xl font-bold text-slate-900 gap-3">
            <div className="bg-indigo-100 p-2.5 rounded-2xl">
              <Factory className="h-6 w-6 text-indigo-600" />
            </div>
            {industryToEdit ? "Editar Industria" : "Crear Nueva Industria"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="industry-name" className="text-slate-700 font-semibold">
                Nombre de la Industria <span className="text-red-500">*</span>
              </Label>
              <Input
                id="industry-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Ej: Tecnología, Retail, Banca"
                disabled={loading}
                className={`h-12 bg-slate-50/50 border-slate-200 rounded-xl focus-visible:bg-white focus-visible:ring-indigo-500/20 transition-all ${
                  error ? "border-red-500 focus-visible:ring-red-500" : ""
                }`}
                autoFocus
              />
              {error && (
                <p className="text-red-500 text-sm font-medium">{error}</p>
              )}
            </div>

            <p className="text-sm text-slate-500 ml-1">
              Este nombre se usará para clasificar empresas en el sistema.
            </p>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-slate-50/80 border border-slate-100 rounded-[16px] transition-colors hover:bg-slate-50 cursor-pointer" onClick={() => !loading && setIsDefault(!isDefault)}>
            <Checkbox
              id="is-default"
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked as boolean)}
              disabled={loading}
              className="mt-0.5"
            />
            <div className="space-y-1.5">
              <Label
                htmlFor="is-default"
                className="text-sm font-semibold text-slate-800 cursor-pointer pointer-events-none"
              >
                Establecer como industria por defecto
              </Label>
              <p className="text-xs text-slate-500 leading-relaxed pointer-events-none">
                La industria por defecto se seleccionará automáticamente al
                crear nuevas empresas.
              </p>
            </div>
          </div>

          <DialogFooter className="pt-4 gap-3 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
              className="flex-1 h-12 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50 font-medium"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="flex-1 h-12 rounded-xl bg-slate-900 hover:bg-black text-white font-medium shadow-sm shadow-slate-900/20"
              disabled={loading || !name.trim() || name.trim().length < 2}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {industryToEdit ? "Guardando..." : "Creando..."}
                </>
              ) : industryToEdit ? (
                "Guardar Cambios"
              ) : (
                "Crear Industria"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
